import PropTypes from 'prop-types'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  resumeResortScopeRecovery,
  suspendResortScopeRecovery,
} from '../api/api'
import {
  login as apiLogin,
  logout as apiLogout,
  clearUserFromLocalStorage,
  getMe,
  persistUserToLocalStorage,
} from '../api/authApi'
import {
  clearActiveResortId,
  getActiveResortId,
  setActiveResortId,
} from '../utils/activeResort'

const AuthContext = createContext(null)

// user.resort_id means the active resort for the front, the API returns the home one
const withActiveResort = user => {
  const active = getActiveResortId()
  return user && active !== null ? { ...user, resort_id: active } : user
}

// the stored active resort may belong to a previous account, keep it only if it is in scope
const reconcileActiveResort = data => {
  const scope = (Array.isArray(data.resortIds) ? data.resortIds : []).filter(
    id => Number.isInteger(id) && id > 0,
  )
  const stored = getActiveResortId()
  if (stored !== null && scope.includes(stored)) return stored
  if (scope.includes(data.activeResortId)) {
    setActiveResortId(data.activeResortId)
    return data.activeResortId
  }
  // nothing usable: drop it, the backend then picks a default resort
  if (stored !== null) clearActiveResortId()
  return null
}

// snapshot for code that runs outside React (api interceptors, sockets)
let currentUserRef = { user: null, permissions: [], enabledModules: [] }
// eslint-disable-next-line react-refresh/only-export-components
export const getCurrentUser = () => currentUserRef.user
// eslint-disable-next-line react-refresh/only-export-components
export const getCurrentUserData = () =>
  currentUserRef.user
    ? {
        user: currentUserRef.user,
        permissions: currentUserRef.permissions,
        enabledModules: currentUserRef.enabledModules,
      }
    : null

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [permissions, setPermissions] = useState([])
  const [enabledModules, setEnabledModules] = useState([])
  const [resortIds, setResortIds] = useState([])
  const [resorts, setResorts] = useState([])
  const [activeResortId, setActiveResort] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loggingOut, setLoggingOut] = useState(false)

  useEffect(() => {
    currentUserRef.user = user
    currentUserRef.permissions = permissions
    currentUserRef.enabledModules = enabledModules
  }, [user, permissions, enabledModules])

  useEffect(() => {
    getMe()
      .then(data => {
        if (data?.user) {
          // reconcile first, the two lines below read the stored resort
          const active = reconcileActiveResort(data)
          setUser(withActiveResort(data.user))
          setPermissions(data.permissions || [])
          setEnabledModules(data.enabledModules || [])
          setResortIds(data.resortIds || [])
          setResorts(data.resorts || [])
          setActiveResort(active)
          persistUserToLocalStorage(
            data.user,
            data.permissions || [],
            data.enabledModules || [],
            data.resortIds || [],
            data.resorts || [],
          )
        } else {
          clearUserFromLocalStorage()
        }
      })
      .catch(() => {
        setUser(null)
        setPermissions([])
        setEnabledModules([])
        setResortIds([])
        setResorts([])
        setActiveResort(null)
        clearUserFromLocalStorage()
      })
      .finally(() => setLoading(false))
  }, [])

  const login = useCallback(async credentials => {
    const data = await apiLogin(credentials)
    resumeResortScopeRecovery()
    const active = reconcileActiveResort(data)
    setUser(withActiveResort(data.user))
    setPermissions(data.permissions || [])
    setEnabledModules(data.enabledModules || [])
    // no reload after login, so the resort switcher needs the scope right away
    setResortIds(data.resortIds || [])
    setResorts(data.resorts || [])
    setActiveResort(active)
    persistUserToLocalStorage(
      data.user,
      data.permissions || [],
      data.enabledModules || [],
      data.resortIds || [],
      data.resorts || [],
    )
    return data
  }, [])

  const logout = useCallback(async () => {
    // the app is covered until the reload below, otherwise the login page flashes twice
    setLoggingOut(true)
    clearUserFromLocalStorage()
    // suspend before clearing, an in-flight 403 recovery would restore the resort
    suspendResortScopeRecovery()
    clearActiveResortId()
    // finally: the redirect must happen even when the backend is down
    try {
      await apiLogout()
    } finally {
      currentUserRef.user = null
      currentUserRef.permissions = []
      currentUserRef.enabledModules = []
      setUser(null)
      setPermissions([])
      setEnabledModules([])
      setResortIds([])
      setResorts([])
      setActiveResort(null)
      window.location.href = '/login'
    }
  }, [])

  // stable identities, consumers put them in effect dependencies
  const hasPermission = useCallback(
    requiredPermission => {
      // '*' is the Super Admin wildcard
      if (permissions.includes('*')) {
        return true
      }
      return permissions.includes(requiredPermission)
    },
    [permissions],
  )

  const hasModule = useCallback(
    requiredModule => {
      if (permissions.includes('*')) {
        return true
      }
      return enabledModules.includes(requiredModule)
    },
    [permissions, enabledModules],
  )

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      permissions,
      enabledModules,
      resortIds,
      resorts,
      activeResortId,
      hasPermission,
      hasModule,
      login,
      logout,
      loading,
      loggingOut,
      setUser,
    }),
    [
      user,
      permissions,
      enabledModules,
      resortIds,
      resorts,
      activeResortId,
      hasPermission,
      hasModule,
      login,
      logout,
      loading,
      loggingOut,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useAuth = () => {
  return useContext(AuthContext)
}
